import React, { useState } from 'react';
import { IconButton, Tooltip, CircularProgress } from '@mui/material';
import {
  Close,
  ErrorOutline,
  WarningAmberOutlined,
  InfoOutlined,
  CheckCircleOutline,
  RefreshOutlined,
  ExpandMore,
  ExpandLess,
} from '@mui/icons-material';
import {
  useActiveNotifications,
  useDismissNotification,
  useTriggerEvaluation,
} from '../../hooks/useNotifications';
import type { Notification } from '../../services/notificationService';
import { useAccountStore } from '../Stores/accountStore';
import { NotificationDataView } from './NotificationDataView';

interface Props {
  onClose: () => void;
}

const severityConfig: Record<string, { icon: React.ReactNode; color: string; bg: string; border: string; rowBg: string; leftBorder: string }> = {
  critical: {
    icon: <ErrorOutline sx={{ fontSize: 18, color: '#ef4444' }} />,
    color: 'text-red-600',
    bg: 'bg-red-50',
    border: 'border-red-200',
    rowBg: '#ffffff',
    leftBorder: '#ef4444',
  },
  warning: {
    icon: <WarningAmberOutlined sx={{ fontSize: 18, color: '#f59e0b' }} />,
    color: 'text-amber-500',
    bg: 'bg-amber-50',
    border: 'border-amber-200',
    rowBg: '#ffffff',
    leftBorder: '#f59e0b',
  },
  info: {
    icon: <InfoOutlined sx={{ fontSize: 18, color: '#3b82f6' }} />,
    color: 'text-blue-500',
    bg: 'bg-blue-50',
    border: 'border-blue-200',
    rowBg: '#ffffff',
    leftBorder: '#3b82f6',
  },
};

function timeAgo(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const minutes = Math.floor(diff / 60000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

const NotificationItem: React.FC<{
  notification: Notification;
  onDismiss: (id: number) => void;
  isDismissing: boolean;
  formulaColor?: string;
}> = ({ notification, onDismiss, isDismissing, formulaColor }) => {
  const config = severityConfig[notification.severity] || severityConfig.info;
  const [expanded, setExpanded] = useState(false);
  const isLong = notification.message.length > 80;

  return (
    <div
      className={`flex border-b border-gray-100 cursor-pointer hover:bg-gray-50 transition-colors ${isDismissing ? 'opacity-50' : ''}`}
      style={{ borderLeft: `3px solid ${formulaColor || config.leftBorder}` }}
      onClick={() => setExpanded(!expanded)}
    >
      <div className="flex gap-2.5 p-3 flex-1 min-w-0">
        <div className="flex-shrink-0 mt-0.5">
          {config.icon}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <p className="text-sm font-medium text-gray-900 leading-tight">
              {(() => {
                const name = notification.formula_name || notification.formula_code || '';
                const title = notification.title;
                return title.startsWith(name + ':') ? title.slice(name.length + 1).trim()
                  : title.startsWith(name) ? title.slice(name.length).replace(/^[:\s-]+/, '').trim()
                  : title;
              })()}
            </p>
            <Tooltip title="Dismiss" arrow placement="left">
              <IconButton
                size="small"
                onClick={(e) => { e.stopPropagation(); onDismiss(notification.id); }}
                disabled={isDismissing}
                sx={{ padding: '2px' }}
              >
                <Close sx={{ fontSize: 14, color: '#9ca3af' }} />
              </IconButton>
            </Tooltip>
          </div>
          <div className="flex items-center gap-1.5 mt-1">
            {(notification.formula_name || notification.formula_code) && (
              <span
                className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold"
                style={{
                  backgroundColor: formulaColor ? `${formulaColor}18` : '#f3f4f6',
                  color: formulaColor || '#6b7280',
                }}
              >
                {notification.formula_name || notification.formula_code}
              </span>
            )}
            <span className="text-[10px] text-gray-400">{timeAgo(notification.created_at)}</span>
          </div>
          <NotificationDataView data={notification.data} />
          {expanded && (
            <p className="text-xs text-gray-600 leading-relaxed mt-1">
              {notification.message}
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export const NotificationPanel: React.FC<Props> = ({ onClose }) => {
  const { data: notifications = [], isLoading, refetch } = useActiveNotifications();
  const dismissMutation = useDismissNotification();
  const evaluateMutation = useTriggerEvaluation();
  const { formulaColorDictionary } = useAccountStore();

  const handleDismiss = (id: number) => {
    dismissMutation.mutate(id);
  };

  const handleEvaluate = () => {
    evaluateMutation.mutate(undefined, {
      onSuccess: () => {
        refetch();
      },
    });
  };

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 bg-gray-50 rounded-t-lg">
        <div className="flex items-center gap-2">
          <h3 className="text-sm font-semibold text-gray-800">Notifications</h3>
          {notifications.length > 0 && (
            <span className="inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-red-100 text-red-700 text-[11px] font-semibold">
              {notifications.length}
            </span>
          )}
        </div>
        <div className="flex items-center gap-1">
          <Tooltip title="Check rules now" arrow>
            <IconButton
              size="small"
              onClick={handleEvaluate}
              disabled={evaluateMutation.isLoading}
              sx={{ color: '#6b7280' }}
            >
              {evaluateMutation.isLoading ? (
                <CircularProgress size={16} />
              ) : (
                <RefreshOutlined sx={{ fontSize: 18 }} />
              )}
            </IconButton>
          </Tooltip>
          <IconButton size="small" onClick={onClose} sx={{ color: '#6b7280' }}>
            <Close sx={{ fontSize: 18 }} />
          </IconButton>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-auto">
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <CircularProgress size={24} />
          </div>
        ) : notifications.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 text-gray-400">
            <CheckCircleOutline sx={{ fontSize: 40, mb: 1, color: '#d1d5db' }} />
            <p className="text-sm font-medium">All clear</p>
            <p className="text-xs mt-0.5">No active notifications</p>
          </div>
        ) : (
          notifications.map((n) => (
            <NotificationItem
              key={n.id}
              notification={n}
              onDismiss={handleDismiss}
              isDismissing={dismissMutation.isLoading && dismissMutation.variables === n.id}
              formulaColor={n.formula_code ? formulaColorDictionary[n.formula_code] : undefined}
            />
          ))
        )}
      </div>

      {/* Footer */}
      {notifications.length > 0 && (
        <div className="px-4 py-2 border-t border-gray-100 bg-gray-50 rounded-b-lg">
          <p className="text-[10px] text-gray-400 text-center">
            Showing {notifications.length} active notification{notifications.length !== 1 ? 's' : ''}
          </p>
        </div>
      )}
    </div>
  );
};
